import { useState, memo, useCallback, useEffect, useRef } from 'react';
import { Button } from './Button';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

export interface CopyButtonProps {
  /** Texto a copiar */
  text: string;
  /** Tamaño del botón */
  size?: 'sm' | 'md' | 'lg';
  /** Variante del botón */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  /** Mostrar solo ícono */
  iconOnly?: boolean;
  /** Clase CSS adicional */
  className?: string;
  /** Texto del tooltip cuando se copia */
  copiedText?: string;
  /** Texto del tooltip antes de copiar */
  copyText?: string;
}

/**
 * CopyButton (memoized) - Botón para copiar texto al portapapeles
 * 
 * Uso:
 * ```tsx
 * <CopyButton text="texto a copiar" />
 * <CopyButton text="texto" iconOnly size="sm" />
 * ```
 */
export const CopyButton = memo(function CopyButton({
  text,
  size = 'sm',
  variant = 'ghost',
  iconOnly = false,
  className,
  copiedText = 'Copied!',
  copyText = 'Copy',
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Memoized copy handler
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(copiedText);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  }, [text, copiedText, toast]);

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleCopy}
      className={cn(className)}
      title={copied ? copiedText : copyText}
    >
      {iconOnly ? (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {copied ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          ) : (
            <>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </>
          )}
        </svg>
      ) : (
        <>
          {copied ? copiedText : copyText}
        </>
      )}
    </Button>
  );
});








